
import React from 'react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Clock, HelpCircle, CheckCircle, Zap } from 'lucide-react';

const ObjectionHandlingSection = () => {
  const { isMobile } = useBreakpoint();

  const objections = [
    {
      concern: "I don't have time to learn something new...",
      response: "The beauty of this system is its simplicity. You can build your demo-ready AI business card in just 15 minutes using our drag-and-drop platform. No complex technical skills required.",
      icon: Clock,
      accentColor: "from-[#003a70] to-[#003a70]/70",
      bgColor: "from-blue-50 to-[#FEE6D8]/30"
    },
    {
      concern: "What if I can't explain the technology?",
      response: "You don't have to! The demo sells itself. When prospects see an AI business card answer questions in real-time and capture leads automatically, they immediately understand the value. The technology does all the explaining for you.",
      icon: HelpCircle,
      accentColor: "from-[#FEE6D8] to-[#FEE6D8]/70",
      bgColor: "from-[#FEE6D8]/40 to-orange-50"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-white to-[#FEE6D8]/20 relative overflow-hidden">
      {/* Enhanced background decoration with more contrast */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-[#003a70]/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-[#FEE6D8]/40 rounded-full blur-3xl"></div>
        <div className="absolute top-2/3 right-1/3 w-48 h-48 bg-gradient-to-r from-[#003a70]/10 to-[#FEE6D8]/30 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="space-y-12">
          
          {/* Section Header with enhanced contrast */}
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#003a70] via-[#003a70]/95 to-[#003a70]/85 rounded-full shadow-xl border-2 border-white/20">
              <HelpCircle className="text-white" size={24} />
              <span className="text-white font-semibold text-lg">Common Concerns</span>
            </div>
            
            <h2 className={cn(
              "font-bold text-gray-900 leading-tight",
              isMobile ? "text-3xl" : "text-4xl lg:text-5xl"
            )}>
              But What If<span className="text-[#003a70]">...</span>
            </h2>
            
            <div className="w-24 h-1 bg-gradient-to-r from-[#003a70] via-[#FEE6D8] to-[#003a70] mx-auto rounded-full shadow-sm"></div>
          </div>

          {/* Objections List with alternating colors */}
          <div className="space-y-8">
            {objections.map((objection, index) => (
              <div
                key={index}
                className={cn(
                  "group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden",
                  "border-2 border-gray-100 hover:border-gray-200"
                )}
              >
                {/* Enhanced gradient accent bar */}
                <div className={cn(
                  "h-2 bg-gradient-to-r",
                  objection.accentColor
                )}></div>
                
                <div className={cn(
                  "p-6 md:p-8 space-y-6"
                )}>
                  
                  {/* Concern with enhanced styling */}
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg border-2 border-white",
                      index === 0 
                        ? "bg-gradient-to-br from-[#003a70] to-[#003a70]/80" 
                        : "bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/80"
                    )}>
                      <objection.icon 
                        className={index === 0 ? "text-white" : "text-[#003a70]"} 
                        size={24} 
                      />
                    </div>
                    
                    <div className="flex-grow">
                      <h3 className={cn(
                        "font-bold leading-tight mb-2",
                        isMobile ? "text-xl" : "text-2xl",
                        index === 0 ? "text-[#003a70]" : "text-orange-800"
                      )}>
                        "{objection.concern}"
                      </h3>
                    </div>
                  </div>

                  {/* Response with alternating background colors */}
                  <div className={cn(
                    "rounded-xl p-6 border-2",
                    index === 0 
                      ? "bg-gradient-to-r from-blue-50 to-[#FEE6D8]/20 border-blue-200/60" 
                      : "bg-gradient-to-r from-[#FEE6D8]/30 to-orange-50 border-[#FEE6D8]/80"
                  )}>
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center shadow-md border-2 border-white",
                        index === 0 
                          ? "bg-gradient-to-br from-blue-100 to-blue-200" 
                          : "bg-gradient-to-br from-[#FEE6D8] to-orange-100"
                      )}>
                        <CheckCircle 
                          className={index === 0 ? "text-[#003a70]" : "text-orange-700"} 
                          size={20} 
                        />
                      </div>
                      
                      <p className={cn(
                        "leading-relaxed flex-grow",
                        isMobile ? "text-base" : "text-lg",
                        index === 0 ? "text-gray-800" : "text-gray-900"
                      )}>
                        {objection.response}
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>

          {/* Bottom Reassurance with enhanced contrast */}
          <div className="text-center bg-gradient-to-br from-[#003a70]/10 via-[#FEE6D8]/40 to-[#FEE6D8]/60 rounded-2xl p-6 md:p-8 border-2 border-[#FEE6D8]/70 shadow-xl">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                <Zap className="text-[#FEE6D8]" size={24} />
              </div>
              <h3 className={cn(
                "font-bold text-[#003a70]",
                isMobile ? "text-xl" : "text-2xl"
              )}>
                The Bottom Line
              </h3>
            </div>
            
            <p className={cn(
              "text-gray-900 leading-relaxed font-medium",
              isMobile ? "text-lg" : "text-xl"
            )}>
              <strong className="text-[#003a70]">This isn't about mastering complex technology.</strong> It's about having a simple tool that creates genuine "wow" moments and turns networking conversations into sales opportunities - automatically.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ObjectionHandlingSection;
